import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { SendPublicationInspectionsAlertParams } from '@hooks/usePublicationInspection'

import type { PublicationInspectionModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action, EntityOptions, Errors, FetchRequestPayload, RootReducerState,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/publicationInspection/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/publicationInspection/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/publicationInspection/FETCH_FAILURE'

const UPDATE_REQUEST = 'realbase/publicationInspection/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/publicationInspection/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/publicationInspection/UPDATE_FAILURE'

export type PublicationInspectionModuleState = {
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: PublicationInspectionModuleState = {
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadPublicationInspections(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().publicationInspections.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/publication_inspections.json', options)
      .then(({ data }: { data: PublicationInspectionModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PUBLICATION_INSPECTION_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function sendPublicationInspectionsAlert(
  params: SendPublicationInspectionsAlertParams,
  options: EntityOptions = {},
) {
  const { agency_id, edition_id } = params

  const config = {
    method: 'POST',
    data: JSON.stringify({
      agency_id,
      edition_id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/publication_inspections/send_alert.json', options, config)
      .then(({ data }: { data: { id: number } }) => {
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure())

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: PublicationInspectionModuleState = initialState,
  action: Action = { type: '' },
): PublicationInspectionModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
